import { TaskState } from '@/types/gelato';
import { GelatoRelay, TransactionStatusResponse } from '@gelatonetwork/relay-sdk';
import { gelatoRelay } from './gelatoRelay';

/**
 * Callback hooks for Gelato task status updates
 */
export interface GelatoTaskStatusHooks {
  onTaskStatusUpdate?: (taskStatus: TransactionStatusResponse) => void;
  onTaskCompleted?: (taskId: string, taskStatus: TransactionStatusResponse) => void;
  onTaskFailed?: (taskId: string, taskStatus: TransactionStatusResponse, reason?: string) => void;
  onTransactionHash?: (taskId: string, transactionHash: string) => void;
}

/**
 * Gelato Event Listener Manager
 * Manages WebSocket connections and task status updates via Gelato SDK
 */
class GelatoEventListenerManager {
  private gelatoRelay: GelatoRelay;
  private taskHooks: Map<string, GelatoTaskStatusHooks> = new Map();
  private isListenerRegistered = false;

  constructor() {
    this.gelatoRelay = gelatoRelay;
    this.setupEventListener();
  }

  /**
   * Setup the global event listener for task status updates
   */
  private setupEventListener() {
    if (this.isListenerRegistered) {
      return;
    }

    this.gelatoRelay.onTaskStatusUpdate((taskStatus: TransactionStatusResponse) => {
      console.log('📡 [Gelato Event] Task status update received:', {
        taskId: taskStatus.taskId,
        taskState: taskStatus.taskState,
        transactionHash: taskStatus.transactionHash,
        chainId: taskStatus.chainId,
      });

      const hooks = this.taskHooks.get(taskStatus.taskId);
      if (!hooks) {
        console.log('⚠️ [Gelato Event] No hooks registered for task:', taskStatus.taskId);
        return;
      }

      // Call general status update hook
      hooks.onTaskStatusUpdate?.(taskStatus);

      // Call transaction hash hook if available
      if (taskStatus.transactionHash) {
        hooks.onTransactionHash?.(taskStatus.taskId, taskStatus.transactionHash);
      }

      // Check if task is in final state
      if (this.isFinalTaskState(taskStatus.taskState as TaskState)) {
        if (taskStatus.taskState === 'ExecSuccess') {
          hooks.onTaskCompleted?.(taskStatus.taskId, taskStatus);
        } else {
          hooks.onTaskFailed?.(
            taskStatus.taskId,
            taskStatus,
            taskStatus.lastCheckMessage || `Task failed with state: ${taskStatus.taskState}`,
          );
        }

        // Clean up hooks for completed tasks
        this.unregisterTaskHooks(taskStatus.taskId);
      }
    });

    this.isListenerRegistered = true;
    console.log('✅ [Gelato Event] Global task status listener registered');
  }

  /**
   * Register hooks for a specific task
   */
  registerTaskHooks(taskId: string, hooks: GelatoTaskStatusHooks) {
    this.taskHooks.set(taskId, hooks);
    console.log('📝 [Gelato Event] Hooks registered for task:', taskId);
    this.gelatoRelay.subscribeTaskStatusUpdate(taskId);
  }

  /**
   * Unregister hooks for a specific task
   */
  unregisterTaskHooks(taskId: string) {
    const removed = this.taskHooks.delete(taskId);
    if (removed) {
      console.log('🗑️ [Gelato Event] Hooks unregistered for task:', taskId);
      this.gelatoRelay.unsubscribeTaskStatusUpdate(taskId);
    }
  }

  /**
   * Get the GelatoRelay instance for making relay calls
   */
  getGelatoRelay(): GelatoRelay {
    return this.gelatoRelay;
  }

  /**
   * Check if task state is final
   */
  private isFinalTaskState(taskState: TaskState): boolean {
    switch (taskState) {
      case TaskState.ExecSuccess:
      case TaskState.ExecReverted:
      case TaskState.Cancelled:
        return true;
      default:
        return false;
    }
  }

  /**
   * Get current number of registered task hooks
   */
  getRegisteredTasksCount(): number {
    return this.taskHooks.size;
  }

  /**
   * Clear all registered task hooks
   */
  clearAllTaskHooks() {
    const count = this.taskHooks.size;
    this.taskHooks.clear();
    console.log(`🧹 [Gelato Event] Cleared ${count} task hooks`);
  }
}

// Create singleton instance
const gelatoEventListenerManager = new GelatoEventListenerManager();

export default gelatoEventListenerManager;

/**
 * Convenience function to register task hooks
 */
export function registerGelatoTaskHooks(taskId: string, hooks: GelatoTaskStatusHooks) {
  gelatoEventListenerManager.registerTaskHooks(taskId, hooks);
}

/**
 * Convenience function to unregister task hooks
 */
export function unregisterGelatoTaskHooks(taskId: string) {
  gelatoEventListenerManager.unregisterTaskHooks(taskId);
}

/**
 * Get the GelatoRelay instance
 */
export function getGelatoRelay(): GelatoRelay {
  return gelatoEventListenerManager.getGelatoRelay();
}
